import AsyncStorage from '@react-native-async-storage/async-storage';
import { IpadLang } from '@w3lcome/types';
import appConfig from '_/config/app';
import Constants from 'expo-constants';
import i18n, { InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';

export interface TranslationParams {
  translationId?: string | null;
}

const supportedLangs = Object.values(IpadLang);

const languageDetector = {
  type: 'languageDetector',
  async: true,
  detect: async (language: any) => {
    const persistedLocale = await AsyncStorage.getItem(appConfig.languageKey);
    if (!persistedLocale) {
      return language(IpadLang.PTBR);
    }
    return language(JSON.parse(persistedLocale).code);
  },
  init: () => {},
  cacheUserLanguage: () => {},
};

async function fetchTranslationJson(url: string): Promise<Record<string, any> | null> {
  try {
    const timestamp = new Date().getTime();
    const separator = url.includes('?') ? '&' : '?';
    const response = await fetch(`${url}${separator}timestamp=${timestamp}`);
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (error) {
    return null;
  }
}

export async function loadHierarchicalTranslations(params?: TranslationParams) {
  const assetsUrl = Constants.expoConfig?.extra?.assetsURL;
  if (!assetsUrl) return;

  const { translationId } = params || {};

  await Promise.all(
    supportedLangs.map(async (lng) => {
      const defaultUrl = `${assetsUrl}/locales/totem/${lng}/translation.json`;
      const defaultData = await fetchTranslationJson(defaultUrl);
      if (defaultData) {
        i18n.addResourceBundle(lng, 'translation', defaultData, true, true);
      }

      if (translationId) {
        const childUrl = `${assetsUrl}/locales/${translationId}/totem/${lng}/translation.json`;
        const childData = await fetchTranslationJson(childUrl);
        if (childData) {
          i18n.addResourceBundle(lng, 'translation', childData, true, true);
        }
      }
    })
  );
}

export async function changeTranslation(params?: string | TranslationParams | null) {
  let translationId: string | null = null;

  if (typeof params === 'string') {
    translationId = params;
  } else if (params && typeof params === 'object') {
    translationId = params.translationId ?? null;
  }

  await loadHierarchicalTranslations({ translationId });

  const currentLang = i18n.language || IpadLang.PTBR;
  await i18n.reloadResources(currentLang);
  await i18n.changeLanguage(currentLang);
}

export async function initI18n(params?: string | TranslationParams | null) {
  const customizationStr = await AsyncStorage.getItem(appConfig.customizationKey);
  const customization = customizationStr ? JSON.parse(customizationStr) : null;

  let translationId: string | null = null;

  if (typeof params === 'string') {
    translationId = params;
  } else if (params && typeof params === 'object') {
    translationId = params.translationId ?? customization?.translationId ?? null;
  } else {
    translationId = customization?.translationId ?? null;
  }

  const initConfig: InitOptions = {
    fallbackLng: IpadLang.EN,
    debug: false,
    defaultNS: 'translation',
    fallbackNS: 'translation',
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
    supportedLngs: supportedLangs,
    nonExplicitSupportedLngs: false,
    load: 'currentOnly',
  };

  if (!i18n.isInitialized) {
    i18n
      .use(languageDetector as any)
      .use(initReactI18next)
      .init(initConfig);
  }

  await loadHierarchicalTranslations({ translationId });

  const currentLang = i18n.language || IpadLang.PTBR;
  await i18n.changeLanguage(currentLang);
}

initI18n();

export default i18n;
